import * as Types from './types';
import * as helpers from './helpers';
import { actionTypes, DATE_FORMAT, TIME_FORMAT, NUMBER_SEPARATOR, CURRENCY_SYMBOL_POSITION } from './constants';

const additionalAppDefaultState: Types.IAdditionalAppData = {
    favoriteGames: [],
    isAppInitExecuted: false,
    isFavoritesFetching: false,
    isQuickDepositAvailable: false,
    isReconnectVisible: false,
    isServerSideRendering: false,
    isChangeLangInProgress: false
};

const initialResourcesMenuDefaultState: Types.IMenuData = {
    menuItems: []
};

const initialResourcesTagsDefaultState: Types.IAppTagsData = {
    tags: []
};

const initialResourcesGamesDefaultState: Types.IGamesData = {
    isGamesLoaded: false,
    gameTypes: [],
    games: undefined,
    gamesArticles: {},
    gamesConfiguration: {},
    gamesFeeds: [],
    gamesProviders: {},
    gamesTilesLayouts: {}
};

const defaultState: Types.TAppState = {
    ...initialResourcesGamesDefaultState,
    ...initialResourcesMenuDefaultState,
    ...initialResourcesTagsDefaultState,
    ...additionalAppDefaultState,
    aboveHeaderWcIdDesktop: '',
    aboveHeaderWcIdMobile: '',
    aboveHeaderWcIdTablet: '',
    accountPage: '',
    allowedCountries: [],
    allowedCurrencies: [],
    allowedLocales: [],
    allowedRedirectHostNames: '',
    animationConfig: {},
    applicationWCID: '',
    azureConfiguration: {},
    balancePage: '',
    betLossLimitActions: {
        betlimitActions: '',
        losslimitActions: ''
    },
    biPlayerSegmentField: '',
    bingoSystemId: '',
    bonusManagementPreferences: {},
    bonusManagementTCPreferences: {},
    bonusesConfiguration: [],
    cashierUrlTemplates: [],
    casino: '',
    casinoName: '',
    cdnUrl: '',
    chatCssUrl: '',
    chatJsUrl: '',
    chatWidget: {
        enabled: false,
        showIcon: false,
        counterPage: '',
        showLaunchIcon: false
    },
    chatWidgetConfiguration: '',
    chatWidgetSource: '',
    clientSkin: '',
    clientType: '',
    clientVersion: '',
    codeInsertion: {
        onAppInit: '',
        onPageRendered: '',
        onPageRendering: ''
    },
    colorSchemeCSSClass: '',
    companyLogo: '',
    cookiesConfiguration: {},
    countryLocaleMapping: [],
    currencySymbolPosition: CURRENCY_SYMBOL_POSITION,
    customFieldIndex: '',
    dataMapperEndpointURL: '',
    dataVerificationStatusFormName: '',
    dateFormat: DATE_FORMAT,
    defaultBingoRegion: '',
    defaultCountry: '',
    defaultCurrency: '',
    defaultLocale: '',
    depositPage: '',
    depositReminderLimits: [],
    desktopBingoPIGamesBaseURL: '',
    desktopCasinoClientBaseURL: '',
    detailsRecovery: {
        useEmailOnly: false,
        isRequiredVerificationEnable: false,
        enableSmsNotifications: false
    },
    dynamicCurrencyTags: {},
    dynamicTags: {},
    emailVerificationParam: '',
    enableAgeVerification: false,
    enableDataVerificationStatusCheck: false,
    enableGamblingSessionLimitPopup: false,
    enableGoogleAnalyticsOAPIEvent: false,
    enableSplashPage: false,
    endpointURL: '',
    facebookClientID: '',
    facebookProxyURL: '',
    facebookSuccessURL: '',
    footerWCID: '',
    galaxyClientType: '',
    galaxyClientVersion: '',
    groupId: 1,
    headerWCID: '',
    http404Configuration: {},
    inboxWidget: {
        enabled: false,
        showIcon: false,
        counterPage: '',
        showLaunchIcon: false
    },
    isAdminNode: false,
    isAutoLoginEnabled: false,
    isEnablePasswordSpy: false,
    isLoginAgainPopup: false,
    isPostcodeEnabled: false,
    isShowSidebar: false,
    isItalianHeaderEnabled: false,
    isUsernameInUppercase: false,
    jackpotsCasino: '',
    languagePage: '',
    leaderboard: {},
    liveEndpointUrl: '',
    liveGuestVipLevel: '',
    localeSeparatorCurrencyMapping: [],
    locationJson: {},
    loginPage: '',
    loginWebContent: '',
    loginChangePassword: '',
    logoutAfterSessionEnd: true,
    lowBalanceAlertEnabled: false,
    lowBalanceAlertWcId: '',
    lowBalanceLimits: [],
    menuWCID: '',
    modifiedDate: 0,
    netVerifyJumioEnabled: false,
    numberSeparator: NUMBER_SEPARATOR,
    openApiDomain: '',
    pageInPopupUrls: [],
    paypalMerchantValue: '',
    pgg2: {
        casino: '',
        endpointUrl: '',
        guestsTracking: false,
        endpointTrackingUrl: ''
    },
    postcodeConfiguration: {
        url: '',
        key: '',
        userName: ''
    },
    postcodeProvider: '',
    quickDepositPage: '',
    quickDepositShowSuggestions: false,
    realMode: 1,
    redirectionRules: {},
    redirectionURLTemplates: [],
    redirects: {},
    registrationURL: '',
    rememberMe: '',
    requestStamp: window.Playtech?.Variables?.modifiedDate || Date.now(),
    rootPage: '',
    sendCashierMessagesToGS: false,
    seo: { useTrailingSlash: false },
    sportsbookRootURLLocaleMapping: {},
    sessionSubscriptionBalances: '',
    sessionSubscriptionTags: '',
    sessionSubscriptionTypes: '',
    sessionTimerWebContent: {},
    showChatWidgetHeaderIcon: false,
    showSessionTicker: false,
    splashPage: '',
    ssoPasDomain: '',
    ssoPasExternalJsUrl: '',
    ssoPasJsURLs: '',
    themeName: '',
    timeFlyingPeriod: '',
    timeFormat: TIME_FORMAT,
    translations: {},
    useIMSClientApi: false,
    useServicesCasinoJackpots: false,
    useServicesVfJackpots: false,
    userSegmentURLType: '',
    usernameEmailSwitcher: false,
    wcIdForNotSuspendedPlayer: '',
    wcIdForSuspendedPlayer: ''
};

/**
 * todo:
 * Implement immer reducer after legacy functionality in app/modules/configuration/configuration.js is migrated to app-react or removed.
 * Legacy module mutates Playtech.Variables with causes exception due to immer state immutability.
 */
export const reducer = (state = defaultState, action: Types.TAppAction): Types.TAppState => {
    switch (action.type) {
        case actionTypes.APP_FETCH: {
            const {
                dateFormat,
                timeFormat,
                clientSkin,
                clientType,
                dynamicTags,
                ssoPasDomain,
                ssoPasJsURLs,
                openApiDomain,
                clientVersion,
                numberSeparator,
                betLossLimitActions,
                ssoPasExternalJsUrl,
                currencySymbolPosition
            } = action.payload.app;

            return {
                ...state,
                ...action.payload.app,
                dateFormat: dateFormat || state.dateFormat,
                timeFormat: timeFormat || state.timeFormat,
                dynamicTags: helpers.parseDynamicTags(dynamicTags),
                numberSeparator: numberSeparator || state.numberSeparator,
                betLossLimitActions: betLossLimitActions || state.betLossLimitActions,
                currencySymbolPosition: currencySymbolPosition || state.currencySymbolPosition,
                ssoPasDomain: helpers.parseSuperDomainEndpoint(ssoPasDomain) || state.ssoPasDomain,
                ssoPasJsURLs: helpers.parseSuperDomainEndpoint(ssoPasJsURLs) || state.ssoPasJsURLs,
                openApiDomain: helpers.parseSuperDomainEndpoint(openApiDomain) || state.openApiDomain,
                clientType: helpers.getOverridableQueryStringParameter('clientType', clientType) || state.clientType,
                ssoPasExternalJsUrl: helpers.parseSuperDomainEndpoint(ssoPasExternalJsUrl) || state.ssoPasExternalJsUrl,
                clientVersion: helpers.getOverridableQueryStringParameter('clientVersion', clientVersion) || state.clientVersion,
                clientSkin: helpers.getOverridableQueryStringParameter('clientSkin', clientSkin) || state.clientSkin,
                isServerSideRendering: helpers.isServerSideRendering()
            };
        }

        case actionTypes.SET_FAVORITE_GAMES: {
            const { favoriteGames, isFavoritesFetching } = action.payload;

            return {
                ...state,
                isFavoritesFetching,
                favoriteGames: favoriteGames?.reverse()
            };
        }

        case actionTypes.UPDATE_FAVORITE_GAMES: {
            const { isFavorite, code } = action.payload;
            const { favoriteGames = [] } = state;

            return {
                ...state,
                favoriteGames: isFavorite
                    ? [code, ...favoriteGames]
                    : favoriteGames.filter(item => item !== code)
            };
        }

        case actionTypes.SET_RG_CONFIG: {
            const { rgConfig } = action.payload;

            return {
                ...state,
                rgConfig
            };
        }

        case actionTypes.UPDATE_HEADER_HEIGHT: {
            const { height, decrement } = action.payload;
            const { headerHeight = 0 } = state;

            return {
                ...state,
                headerHeight: decrement ? headerHeight - height : headerHeight + height
            };
        }

        case actionTypes.SET_IS_QUICK_DEPOSIT_AVAILABLE: {
            return {
                ...state,
                ...action.payload
            };
        }

        case actionTypes.ADD_TAGS: {
            return {
                ...state,
                tags: action.payload
            };
        }

        case actionTypes.TOGGLE_IS_APP_INIT_EXECUTED: {
            return {
                ...state,
                isAppInitExecuted: action.payload
            };
        }

        case actionTypes.TOGGLE_RECONNECT: {
            return {
                ...state,
                isReconnectVisible: action.payload
            };
        }

        case actionTypes.UPDATE_MENU_ITEMS: {
            return {
                ...state,
                menuItems: action.payload
            };
        }

        case actionTypes.UPDATE_GAMES_DATA: {
            return {
                ...state,
                ...action.payload,
                isGamesLoaded: true
            };
        }

        case actionTypes.UPDATE_SPORTSBOOK_ROOT_URL: {
            return {
                ...state,
                ...action.payload,
                isSportsbookRootURLReady: true
            };
        }

        case actionTypes.TOGGLE_IS_CHANGE_LANG_IN_PROGRESS: {
            return {
                ...state,
                isChangeLangInProgress: action.payload
            };
        }

        default:
            return state;
    }
};
